import type { ChangeEventHandler } from 'react'
import { Fragment, useCallback, useState, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useAppSelector } from 'hooks/reduxHooks'
import config from 'constants/config'
import ExperimentItem from './ExperimentItem'
import ChertoExperimentItem from './ChertoExperimentItem'
import FeatureFlagItem from './FeatureFlagItem'
import SearchFormTextField from 'components/SearchV2/Components/SearchFormField/SearchFormTextField'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { EmptyObject, objectEntries } from 'lib/object/objectUtils'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import BodyText from 'LuxKit/Typography/BodyText'
import Divider from 'LuxKit/Divider'
import { sortBy } from 'lib/array/arrayUtils'
import Accordion from 'LuxKit/Accordion'
import type { ChertoExperimentEntry, ExperimentEntry, FeatureFlagEntry } from './ExperimentPanelHelpers'
import { filterExperimentEntry, getChertoExperimentConfig } from './ExperimentPanelHelpers'

function ExperimentControlPanel() {
  const intl = useIntl()
  const optimizelyFeatureFlags = useAppSelector(state => state.optimizely.optimizelyFeatureFlags ?? EmptyObject)
  const optimizelyExperiments = useAppSelector(state => state.optimizely.optimizelyExperiments ?? EmptyObject)
  const chertoExperiments = useAppSelector(state => state.cherto.chertoExperiments ?? EmptyObject)
  const [searchValue, setSearchValue] = useState('')

  const experimentList = useMemo<Array<ExperimentEntry>>(() => {
    return sortBy(
      objectEntries(optimizelyExperiments),
      ([id]) => id,
      'asc',
    ).filter((entry): entry is ExperimentEntry => {
      const [, exp] = entry
      return exp !== undefined
    })
  }, [optimizelyExperiments])

  const chertoExperimentList = useMemo<Array<ChertoExperimentEntry>>(() => {
    return sortBy(
      (config.CHERTO_EXPERIMENTS ?? []).flatMap((experimentId) => {
        const experimentConfig = getChertoExperimentConfig(experimentId)
        if (!experimentConfig) {
          return []
        }

        return [[experimentId, chertoExperiments[experimentId], experimentConfig] as ChertoExperimentEntry]
      }),
      ([experimentId]) => experimentId,
      'asc',
    )
  }, [chertoExperiments])

  const featureFlagList = useMemo<Array<FeatureFlagEntry>>(() => {
    return sortBy(
      objectEntries(optimizelyFeatureFlags),
      ([id]) => id,
      'asc',
    ).filter((entry): entry is FeatureFlagEntry => entry !== undefined)
  }, [optimizelyFeatureFlags])

  const filteredExperiments = useMemo<Array<ExperimentEntry>>(() => {
    return experimentList.filter(filterExperimentEntry(searchValue))
  }, [searchValue, experimentList])

  const filteredChertoExperiments = useMemo<Array<ChertoExperimentEntry>>(() => {
    return chertoExperimentList.filter(filterExperimentEntry(searchValue))
  }, [chertoExperimentList, searchValue])

  const filteredFeatureFlags = useMemo<Array<FeatureFlagEntry>>(() => {
    return featureFlagList.filter(filterExperimentEntry(searchValue))
  }, [featureFlagList, searchValue])

  const hasFeatureFlags = Object.keys(optimizelyFeatureFlags).length > 0
  const hasExperiments = Object.keys(optimizelyExperiments).length > 0
  const hasChertoExperiments = chertoExperimentList.length > 0

  const handleClearSearch = useCallback(() => {
    setSearchValue('')
  }, [])

  const handleSearchChange = useCallback<ChangeEventHandler<HTMLInputElement>>((e) => {
    setSearchValue(e.target.value)
  }, [])

  return (
    <ModalBase mode="drawer-starboard">
      <ModalHeader title={intl.formatMessage({
        id: 'temp_experiment_control_panel_title',
        defaultMessage: 'AB Control Panel',
        description: 'Modal title for the internal Optimizely experiment and feature flag control panel',
      })}
      >
        <SearchFormTextField
          label={intl.formatMessage({
            id: 'temp_experiment_control_panel_search_label',
            defaultMessage: 'Search experiments',
            description: 'Search field label in the internal experiment control panel for Optimizely entries',
          })}
          placeholder={intl.formatMessage({
            id: 'temp_experiment_control_panel_search_placeholder',
            defaultMessage: 'search by key or name',
            description: 'Search field placeholder in the internal experiment control panel for filtering entries',
          })}
          value={searchValue}
          onChange={handleSearchChange}
          onClear={handleClearSearch}
        />
      </ModalHeader>
      <ModalBody>
        <ModalContent>
          <Accordion
            heading={intl.formatMessage({
              id: 'temp_experiment_control_panel_experiments_heading',
              defaultMessage: 'Experiments',
              description: 'Accordion heading for the experiments section in the internal Optimizely control panel',
            })}
            defaultOpen
            hideDivider
            size="large"
          >
            {!hasExperiments && <BodyText variant="large">{intl.formatMessage({
              id: 'temp_experiment_control_panel_no_experiments',
              defaultMessage: 'No experiments found.',
              description: 'Empty-state message when there are no experiments available in the internal Optimizely control panel',
            })}
            </BodyText>}

            {hasExperiments && (
              <VerticalSpacer gap={12}>
                {filteredExperiments.length === 0 && <BodyText variant="large">{intl.formatMessage({
                  id: 'temp_experiment_control_panel_no_matching_experiments',
                  defaultMessage: 'No matching experiments',
                  description: 'Empty-state message when no experiments match the search term in the internal Optimizely control panel',
                })}
                </BodyText>}
                {filteredExperiments.map(([experimentId, experiment], index) => (
                  <Fragment key={experimentId}>
                    {index > 0 && <Divider kind="secondary" />}
                    <ExperimentItem
                      experimentId={experimentId}
                      experiment={experiment}
                    />
                  </Fragment>
                ))}
              </VerticalSpacer>
            )}
          </Accordion>
          <Accordion
            heading={intl.formatMessage({
              id: 'temp_experiment_control_panel_cherto_experiments_heading',
              defaultMessage: 'Cherto Experiments',
              description: 'Accordion heading for the Cherto experiments section in the internal experiment control panel',
            })}
            defaultOpen
            hideDivider
            size="large"
          >
            {!hasChertoExperiments && <BodyText variant="large">{intl.formatMessage({
              id: 'temp_experiment_control_panel_no_cherto_experiments',
              defaultMessage: 'No Cherto experiments found.',
              description: 'Empty-state message when there are no Cherto experiments available in the internal experiment control panel',
            })}
            </BodyText>}

            {hasChertoExperiments && (
              <VerticalSpacer gap={12}>
                {filteredChertoExperiments.length === 0 && <BodyText variant="large">{intl.formatMessage({
                  id: 'temp_experiment_control_panel_no_matching_cherto_experiments',
                  defaultMessage: 'No matching Cherto experiments',
                  description: 'Empty-state message when no Cherto experiments match the search term in the internal experiment control panel',
                })}
                </BodyText>}
                {filteredChertoExperiments.map(([experimentId, experiment, experimentConfig], index) => (
                  <Fragment key={experimentId}>
                    {index > 0 && <Divider kind="secondary" />}
                    <ChertoExperimentItem
                      experimentId={experimentId}
                      experiment={experiment}
                      experimentConfig={experimentConfig}
                    />
                  </Fragment>
                ))}
              </VerticalSpacer>
            )}
          </Accordion>
          <Accordion
            heading={intl.formatMessage({
              id: 'temp_experiment_control_panel_feature_flags_heading',
              defaultMessage: 'Feature Flags',
              description: 'Accordion heading for the feature flags section in the internal Optimizely control panel',
            })}
            defaultOpen
            hideDivider
            size="large"
          >
            {!hasFeatureFlags && <BodyText variant="large">{intl.formatMessage({
              id: 'temp_experiment_control_panel_no_feature_flags',
              defaultMessage: 'No feature flags found.',
              description: 'Empty-state message when there are no feature flags available in the internal Optimizely control panel',
            })}
            </BodyText>}

            {hasFeatureFlags && (
              <VerticalSpacer gap={12}>
                {filteredFeatureFlags.length === 0 && <BodyText variant="large">{intl.formatMessage({
                  id: 'temp_experiment_control_panel_no_matching_feature_flags',
                  defaultMessage: 'No matching feature flags',
                  description: 'Empty-state message when no feature flags match the search term in the internal Optimizely control panel',
                })}
                </BodyText>}
                {filteredFeatureFlags.map(([flagKey, enabled], index) => (
                  <Fragment key={flagKey}>
                    {index > 0 && <Divider kind="secondary" />}
                    <FeatureFlagItem flagKey={flagKey} enabled={enabled} />
                  </Fragment>
                ))}
              </VerticalSpacer>
            )}
          </Accordion>
        </ModalContent>
      </ModalBody>
    </ModalBase>
  )
}

export default ExperimentControlPanel
